import classNames from 'classnames'
import { ErrorMessage, useField } from 'formik'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'

import type { ReactElement } from 'react'
import type { FileWithPath, Accept } from 'react-dropzone'
import type ReactPlayer from 'react-player'

import Button from '~/components/Buttons/Button'
import Dropzone from '~/components/Dropzone'
import ImagePreview from '~/components/ImagePreview'
import Label from '~/components/Label'
import VideoPreview from '~/components/VideoPreview'
import { getFileType } from '~/utils/file'

import './styles.scss'
import '../input-common.scss'

interface Props {
  accept?: Accept
  hasRemove?: boolean
  label?: string
  multiple?: boolean
  name: string
  onVideoPreviewReady?: (player: ReactPlayer) => void
  required?: boolean
}

const FileUploadInput = ({
  accept,
  hasRemove,
  label,
  multiple = true,
  name,
  onVideoPreviewReady,
  required,
}: Props): ReactElement => {
  /** Local state */

  const [, { error, touched }, { setValue }] = useField(name)

  const [file, setFile] = useState<FileWithPath>()

  const [blob, setBlob] = useState<string>()

  /** Handlers */

  const handleDrop = (file: FileWithPath[]) => {
    setFile(file[0])
    setBlob(URL.createObjectURL(file[0]))
    setValue(file[0])
  }

  const handleChangeVideo = () => open()

  const handleRemoveVideo = () => {
    setFile(undefined)
    setBlob(undefined)
  }

  /** Effects */

  const { open } = useDropzone({ accept, onDrop: handleDrop })

  /** Render */

  return (
    <div className="input-component file-upload-component input-container">
      {label ? <Label label={label} required={required} /> : null}
      <div className="file-upload-container">
        {file && blob ? (
          getFileType(file.type) === 'video' ? (
            <VideoPreview filename={file.name} onReady={onVideoPreviewReady} url={blob} />
          ) : (
            <ImagePreview src={blob} />
          )
        ) : null}
        <Dropzone
          accept={accept}
          className={classNames('dropzone-input', { hidden: file && blob })}
          multiple={multiple}
          onDrop={handleDrop}
        />
        {touched && error ? <ErrorMessage className="error-message" component="span" name={name} /> : null}
        {file ? (
          <div className="action-buttons-container">
            <Button onClick={handleChangeVideo} outlined size="small" type="button">
              Change file
            </Button>
            {hasRemove && (
              <Button onClick={handleRemoveVideo} size="small" type="button">
                Remove file
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default FileUploadInput
